import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../config/axiosConfig';
import { useAuth } from '../hooks/useAuth';
import moment from 'moment';
import { toAmsterdam, amsterdamDateTime, formatAmsterdam } from '../utils/time';
import './EditReservation.css';

interface Reservation {
  id: number;
  // Het korte reserveringsnummer (DF-XXXX-XXXX); leeg bij oude records zonder backfill.
  reference_code?: string | null;
  bike_id: number;
  start_at: string;
  end_at: string;
  status: string;
  reserver_name: string;
  reserver_email: string;
  reserver_phone: string;
  notes?: string;
  cancellation_reason?: string;
  // Gevuld als deze keer bij een wekelijks herhalende reservering hoort.
  series_id?: number | null;
}

/** Raakt een wijziging of annulering alleen deze keer, of de hele reeks? */
type Scope = 'single' | 'series';

interface Bike {
  id: number;
  asset_code: string;
  bike_type_name: string;
  location_name: string;
  location_id: number;
  status: string;
}

/** Bezette periode van een fiets, bewust zonder persoonsgegevens. */
interface BusyPeriod {
  id: number;
  bike_id: number;
  start_at: string;
  end_at: string;
  status: string;
}

interface StartTimeOption {
  start: string;
  display: string;
  max_duration: number;
  available: boolean;
}

interface AvailableTimeSlots {
  bike_id: number;
  date: string;
  business_hours: {
    start: string;
    end: string;
  };
  start_time_options: StartTimeOption[];
}

interface ApiResponse<T> {
  data: T;
  success: boolean;
  error?: string;
  message?: string;
}

/** Reserveringen zonder code vallen terug op het volgnummer. */
const referenceLabel = (reservation: Reservation): string =>
  reservation.reference_code ? reservation.reference_code : `#${reservation.id}`;

const EditReservation: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState<string>('');
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [bikes, setBikes] = useState<Bike[]>([]);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [editForm, setEditForm] = useState({
    selectedDate: '',
    start_at: '',
    duration: '',
    notes: ''
  });
  const [availableTimeSlots, setAvailableTimeSlots] = useState<AvailableTimeSlots | null>(null);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState<boolean>(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingReservation, setCancellingReservation] = useState<Reservation | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');
  // Bij een herhalende reservering kiest de gebruiker of het alleen deze keer
  // raakt of alle nog komende keren. Standaard alleen deze keer, want dat is de
  // kleinste ingreep.
  const [cancelScope, setCancelScope] = useState<Scope>('single');
  const [editScope, setEditScope] = useState<Scope>('single');

  // Helper function to format duration in Dutch
  const formatDuration = (hours: number): string => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    const minuteLabel = minutes === 1 ? '1 minuut' : `${minutes} minuten`;

    if (wholeHours === 0) {
      return minuteLabel;
    }
    if (minutes === 0) {
      return `${wholeHours} uur`;
    }
    return `${wholeHours} uur en ${minuteLabel}`;
  };

  const fetchBikes = async (): Promise<void> => {
    try {
      const response = await apiClient.get<ApiResponse<Bike[]>>('/bikes');
      setBikes(response.data.data || []);
    } catch (err) {
      console.error('Error fetching bikes:', err);
    }
  };

  const fetchAvailableTimeSlots = async (bikeId: number, date: string, excludeReservationId?: number, currentReservation?: Reservation): Promise<void> => {
    if (!bikeId || !date) return;
    
    setIsLoadingTimeSlots(true);
    try {
      const params = new URLSearchParams({
        bike_id: bikeId.toString(),
        date: date
      });
      
      if (excludeReservationId) {
        params.append('exclude_reservation_id', excludeReservationId.toString());
      }
      
      const response = await apiClient.get<ApiResponse<AvailableTimeSlots>>(`/reservations/slots/available?${params}`);
      let timeSlots = response.data.data;
      
      // Haal alle reserveringen op voor deze fiets op deze datum om correcte max_duration te berekenen
      let allReservationsForBike: BusyPeriod[] = [];
      if (currentReservation && excludeReservationId) {
        try {
          // Alleen de bezette tijden van deze fiets, zonder persoonsgegevens.
          const busyResponse = await apiClient.get<ApiResponse<BusyPeriod[]>>(
            `/reservations/bike/${bikeId}/busy`
          );
          allReservationsForBike = (busyResponse.data.data || []).filter(res =>
            res.id !== excludeReservationId &&
            toAmsterdam(res.start_at).format('YYYY-MM-DD') === date
          );
        } catch (err) {
          console.error('Error fetching busy periods:', err);
        }
      }
      
      // Als we een reservering aan het bewerken zijn, voeg de huidige tijd altijd toe aan de lijst
      // Dit is ALLEEN voor het bewerken van bestaande reserveringen, NIET voor het aanmaken van nieuwe
      if (currentReservation && excludeReservationId) {
        const currentStartMomentAms = toAmsterdam(currentReservation.start_at);
        const currentDate = currentStartMomentAms.format('YYYY-MM-DD');
        
        // Alleen toevoegen als de datum overeenkomt
        if (currentDate === date) {
          // Bereken de huidige duur
          const currentEndAms = toAmsterdam(currentReservation.end_at);
          const currentDuration = currentEndAms.diff(currentStartMomentAms, 'hours', true);
          
          // Check of de huidige tijd al in de lijst staat (vergelijk datum en tijd in Amsterdamse tijd, niet exacte timestamp)
          const existingOption = timeSlots.start_time_options.find(option => {
            const optionMoment = toAmsterdam(option.start);
            const currentMomentAms = toAmsterdam(currentReservation.start_at);
            return optionMoment.format('YYYY-MM-DD HH:mm') === currentMomentAms.format('YYYY-MM-DD HH:mm');
          });
          
          if (!existingOption) {
            // Voeg de huidige tijd toe aan de lijst met de exacte ISO string van de reservering
            const displayTime = toAmsterdam(currentReservation.start_at).format('HH:mm');
            
            // Bereken de max_duration op basis van de volgende RESERVERING (niet alleen beschikbare tijden)
            let maxDuration = 7; // Default naar 7 uur als er geen volgende reservering is
            
            // Zoek de eerstvolgende reservering die na de huidige start tijd begint
            if (allReservationsForBike.length > 0) {
              const nextReservation = allReservationsForBike
                .filter(res => toAmsterdam(res.start_at).isAfter(toAmsterdam(currentReservation.start_at)))
                .sort((a, b) => toAmsterdam(a.start_at).valueOf() - toAmsterdam(b.start_at).valueOf())[0];
              
              if (nextReservation) {
                // Bereken het verschil tussen huidige start en de start van de volgende reservering
                // Trek 30 minuten (0.5 uur) af voor de verplichte buffer tussen reserveringen
                const diffHours = toAmsterdam(nextReservation.start_at).diff(toAmsterdam(currentReservation.start_at), 'hours', true);
                const maxDurationWithBuffer = diffHours - 0.5; // 30 minuten buffer
                
                // Als de berekende max met buffer korter is dan de huidige duur, 
                // betekent dat er overlap is en moeten we de gebruiker dat laten weten
                // maar we staan de huidige duur toe (anders kan de reservering niet geladen worden)
                if (maxDurationWithBuffer < currentDuration) {
                  maxDuration = currentDuration; // Sta de huidige duur toe
                } else {
                  maxDuration = Math.min(Math.max(maxDurationWithBuffer, 0.25), 7); // Min 15 min, max 7 uur
                }
              }
            }
            
            // Check ook de business hours eind tijd (geen buffer nodig aan het einde van de dag)
            if (timeSlots.business_hours && timeSlots.business_hours.end) {
              const businessEndTime = amsterdamDateTime(`${date} ${timeSlots.business_hours.end}`);
              const hoursUntilClose = businessEndTime.diff(toAmsterdam(currentReservation.start_at), 'hours', true);
              maxDuration = Math.min(maxDuration, hoursUntilClose);
            }
            
            // Als er geen volgende reservering is, zorg dat de max_duration minstens de huidige duur is
            if (allReservationsForBike.length === 0 || !allReservationsForBike.some(res => 
              toAmsterdam(res.start_at).isAfter(toAmsterdam(currentReservation.start_at))
            )) {
              maxDuration = Math.max(maxDuration, currentDuration);
            }
            
            const currentTimeOption: StartTimeOption = {
              start: currentReservation.start_at,
              display: displayTime,
              max_duration: maxDuration,
              available: true
            };
            
            // Voeg toe en sorteer de lijst op tijd
            timeSlots.start_time_options.push(currentTimeOption);
          } else {
            // Als de optie bestaat, zorg dat deze beschikbaar is en de max_duration klopt
            existingOption.available = true;
            // Update max_duration als de huidige duur groter is
            if (currentDuration > existingOption.max_duration) {
              existingOption.max_duration = currentDuration;
            }
          }
          
          // Sorteer alle tijden chronologisch
          timeSlots.start_time_options.sort((a, b) => {
            return moment(a.start).valueOf() - moment(b.start).valueOf();
          });
        }
      }
      
      setAvailableTimeSlots(timeSlots);
    } catch (err) {
      console.error('Error fetching available time slots:', err);
      setAvailableTimeSlots(null);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  const searchReservations = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);
    setReservations([]);
    setEditingReservation(null);
    setHasSearched(true);

    try {
      // De server zoekt op e-mailadres en geeft alleen de eigen reserveringen
      // terug, zodat er nooit gegevens van anderen in de browser belanden.
      const response = await apiClient.post<ApiResponse<Reservation[]>>('/reservations/lookup', {
        email: email.trim()
      });
      const userReservations = response.data.data || [];
      
      setReservations(userReservations);
      
      if (userReservations.length === 0) {
        setError('Geen reserveringen gevonden voor dit email adres.');
      } else {
        setSuccess(`${userReservations.length} reservering(en) gevonden.`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Er is een fout opgetreden';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (reservation: Reservation): void => {
    setEditingReservation(reservation);
    setEditScope('single');
    
    // Datum en duur bepalen in Amsterdamse tijd, gelijk aan hoe de tijden getoond worden.
    const startAms = toAmsterdam(reservation.start_at);
    const endAms = toAmsterdam(reservation.end_at);
    const startDate = startAms.format('YYYY-MM-DD');
    
    // Calculate duration
    const durationHours = endAms.diff(startAms, 'hours', true);
    
    setEditForm({
      selectedDate: startDate,
      start_at: reservation.start_at,
      duration: durationHours.toString(),
      notes: reservation.notes || ''
    });
    
    // Fetch available time slots for the current bike and date
    // Pass the reservation so the current time is always included
    fetchAvailableTimeSlots(reservation.bike_id, startDate, reservation.id, reservation);
  };

  const cancelEditing = (): void => {
    setEditingReservation(null);
    setEditForm({
      selectedDate: '',
      start_at: '',
      duration: '',
      notes: ''
    });
    setAvailableTimeSlots(null);
  };

  const updateReservation = async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate that all required fields are filled
      if (!editForm.start_at || !editForm.duration || !editingReservation) {
        setError('Vul alle verplichte velden in (start tijd en duur).');
        setLoading(false);
        return;
      }

      const appliesToSeries = editScope === 'series' && Boolean(editingReservation.series_id);

      // De server staat bij een hele reeks alleen een andere tijd toe. Het
      // datumveld staat daarom op slot, maar we controleren het hier ook: één
      // keer verzetten en de rest laten staan is voor niemand te volgen.
      if (appliesToSeries && editForm.selectedDate !== toAmsterdam(editingReservation.start_at).format('YYYY-MM-DD')) {
        setError('Bij alle komende keren kun je alleen de tijd aanpassen, niet de datum. Wil je een andere dag? Annuleer dan de komende keren en plan een nieuwe herhaling.');
        setLoading(false);
        return;
      }
      
      // Validate duration against available time
      if (availableTimeSlots) {
        const selectedStartTime = availableTimeSlots.start_time_options.find(
          option => option.start === editForm.start_at
        );
        
        if (selectedStartTime) {
          const requestedDuration = parseFloat(editForm.duration);
          if (requestedDuration > selectedStartTime.max_duration) {
            setError(`Geselecteerde duur (${formatDuration(requestedDuration)}) overschrijdt de maximale beschikbare duur (${formatDuration(selectedStartTime.max_duration)}) voor deze start tijd.`);
            setLoading(false);
            return;
          }
        }
      }
      
      // Calculate end time from start time and duration
      const startTime = moment(editForm.start_at);
      const durationHours = parseFloat(editForm.duration);
      const endTime = startTime.clone().add(durationHours, 'hours');
      
      // Use PUT endpoint as per backend API
      // Include all fields from the current reservation (naam, email, telefoon blijven hetzelfde)
      // Het e-mailadres bewijst dat deze reservering van jou is; de server
      // controleert dat voordat er iets gewijzigd wordt.
      const response = await apiClient.put<ApiResponse<void>>(`/reservations/${id}`, {
        start_at: startTime.toISOString(),
        end_at: endTime.toISOString(),
        reserver_email: editingReservation.reserver_email,
        scope: appliesToSeries ? 'series' : 'single'
      });
      
      setSuccess(response.data.message || 'Reservering succesvol bijgewerkt!');
      
      setEditingReservation(null);
      setEditForm({
        selectedDate: '',
        start_at: '',
        duration: '',
        notes: ''
      });
      setAvailableTimeSlots(null);
      
      // Refresh the search
      setTimeout(() => {
        searchReservations(new Event('submit') as any);
      }, 1000);
      
    } catch (err: any) {
      let errorMessage = 'Er is een fout opgetreden';
      
      if (err.response) {
        if (err.response.data && err.response.data.error) {
          if (err.response.data.details && Array.isArray(err.response.data.details)) {
            errorMessage = `Validatiefout: ${err.response.data.details.join(', ')}`;
          } else {
            errorMessage = err.response.data.error;
          }
        } else if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else {
          errorMessage = `Serverfout: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMessage = 'Geen reactie van de server';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const deleteReservation = async (id: number): Promise<void> => {
    if (!window.confirm('Weet je zeker dat je deze reservering wilt verwijderen?')) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await apiClient.delete<ApiResponse<void>>(`/reservations/${id}`);
      setSuccess('Reservering succesvol verwijderd!');
      
      // Remove from local state
      setReservations(prev => prev.filter(res => res.id !== id));
      
      // Refresh the search
      setTimeout(() => {
        searchReservations(new Event('submit') as any);
      }, 1000);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Er is een fout opgetreden';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openCancelModal = (reservation: Reservation): void => {
    setCancellingReservation(reservation);
    setCancellationReason('');
    setCancelScope('single');
    setShowCancelModal(true);
  };

  const cancelReservation = async (): Promise<void> => {
    if (!cancellingReservation || !cancellationReason.trim()) {
      setError('Reden voor annulering is verplicht');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const cancelsWholeSeries = cancelScope === 'series' && Boolean(cancellingReservation.series_id);

      const response = await apiClient.patch<ApiResponse<void>>(
        `/reservations/${cancellingReservation.id}/cancel`,
        {
          cancellation_reason: cancellationReason.trim(),
          reserver_email: cancellingReservation.reserver_email,
          scope: cancelsWholeSeries ? 'series' : 'single'
        }
      );

      setSuccess(response.data.message || 'Reservering succesvol geannuleerd!');
      setShowCancelModal(false);
      setCancellingReservation(null);
      setCancellationReason('');

      // Update the local state. Bij een hele reeks weten we hier niet welke keren
      // de server precies heeft geraakt, dus zetten we alleen deze keer om; de
      // vernieuwing hieronder haalt de rest op.
      setReservations(prev => prev.map(res =>
        res.id === cancellingReservation.id
          ? { ...res, status: 'cancelled', cancellation_reason: cancellationReason.trim() }
          : res
      ));
      
      // Refresh the search
      setTimeout(() => {
        searchReservations(new Event('submit') as any);
      }, 1000);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Er is een fout opgetreden';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const updateReservationStatus = async (id: number, newStatus: string): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      await apiClient.patch<ApiResponse<void>>(`/reservations/${id}/status`, { status: newStatus });
      
      // Update the local state
      setReservations(prev => prev.map(res => 
        res.id === id 
          ? { ...res, status: newStatus }
          : res
      ));
      
      setSuccess('Status succesvol bijgewerkt!');
      
      // Refresh the search
      setTimeout(() => {
        searchReservations(new Event('submit') as any);
      }, 1000);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Er is een fout opgetreden';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getBikeInfo = (bikeId: number): string => {
    const bike = bikes.find(b => b.id === bikeId);
    return bike ? `${bike.asset_code} (${bike.bike_type_name})` : `Fiets ID: ${bikeId}`;
  };

  const getLocationInfo = (bikeId: number): string => {
    const bike = bikes.find(b => b.id === bikeId);
    return bike ? bike.location_name : 'Onbekend';
  };

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'confirmed': return 'status-confirmed';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      case 'no_show': return 'status-no-show';
      default: return '';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending': return 'In afwachting';
      case 'confirmed': return 'Bevestigd';
      case 'completed': return 'Voltooid';
      case 'cancelled': return 'Geannuleerd';
      case 'no_show': return 'Niet verschenen';
      default: return status;
    }
  };

  React.useEffect(() => {
    fetchBikes();
  }, []);

  return (
    <div className="edit-reservation-page">
      <div className="edit-reservation-container">
        <div className="header">
          <button 
            className="back-button"
            onClick={() => navigate('/')}
          >
            ← Terug
          </button>
          <h1>Reservering Wijzigen</h1>
          <p>Zoek en wijzig je bestaande reserveringen</p>
        </div>

        {error && (
          <div className="error-message">
            <strong>Fout:</strong> {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            <strong>Succes!</strong> {success}
          </div>
        )}

        <div className="search-section">
          <form onSubmit={searchReservations} className="search-form">
            <div className="search-input-group">
              <label htmlFor="email">Email Adres *</label>
              <div className="search-input-row">
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (!e.target.value.trim()) {
                      setHasSearched(false);
                      setReservations([]);
                      setError(null);
                      setSuccess(null);
                    }
                  }}
                  placeholder="Voer je email adres in"
                  required
                />
                <button 
                  type="submit" 
                  className="button primary"
                  disabled={loading}
                >
                  {loading ? 'Zoeken...' : 'Zoeken'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {reservations.length > 0 && (
          <div className="reservations-section">
            <h3>Gevonden Reserveringen ({reservations.length})</h3>
            <div className="reservations-list">
              {reservations.map(reservation => (
                <div key={reservation.id} className="reservation-card">
                  <div className="reservation-header">
                    <h4>Reservering {referenceLabel(reservation)}</h4>
                    <span className={`status-badge ${getStatusClass(reservation.status)}`}>
                      {getStatusLabel(reservation.status)}
                    </span>
                  </div>

                  {reservation.series_id && (
                    <p className="series-badge">
                      🔁 Onderdeel van een wekelijks herhalende reservering
                    </p>
                  )}
                  
                  <div className="reservation-details">
                    <div className="detail-row">
                      <span className="detail-label">Fiets:</span>
                      <span className="detail-value">{getBikeInfo(reservation.bike_id)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Locatie:</span>
                      <span className="detail-value">{getLocationInfo(reservation.bike_id)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Start:</span>
                      <span className="detail-value">
                        {formatAmsterdam(reservation.start_at, 'DD-MM-YYYY HH:mm')}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Einde:</span>
                      <span className="detail-value">
                        {formatAmsterdam(reservation.end_at, 'DD-MM-YYYY HH:mm')}
                      </span>
                    </div>
                    {reservation.notes && (
                      <div className="detail-row">
                        <span className="detail-label">Opmerkingen:</span>
                        <span className="detail-value">{reservation.notes}</span>
                      </div>
                    )}
                    {reservation.cancellation_reason && (
                      <div className="detail-row">
                        <span className="detail-label">Annuleringsreden:</span>
                        <span className="detail-value cancellation-reason">{reservation.cancellation_reason}</span>
                      </div>
                    )}
                  </div>

                  {editingReservation?.id === reservation.id ? (
                    <div className="edit-form">
                      <h5>Reservering Bewerken</h5>

                      {reservation.series_id && (
                        <fieldset className="scope-choice">
                          <legend>Wat wil je wijzigen?</legend>
                          <label>
                            <input
                              type="radio"
                              name={`edit-scope-${reservation.id}`}
                              checked={editScope === 'single'}
                              onChange={() => setEditScope('single')}
                            />
                            <span>Alleen deze keer</span>
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`edit-scope-${reservation.id}`}
                              checked={editScope === 'series'}
                              onChange={() => {
                                setEditScope('series');
                                // De datum kan bij een hele reeks niet mee, dus zetten
                                // we hem terug op de oorspronkelijke dag.
                                const originalDate = toAmsterdam(reservation.start_at).format('YYYY-MM-DD');
                                if (editForm.selectedDate !== originalDate) {
                                  setEditForm(prev => ({
                                    ...prev,
                                    selectedDate: originalDate,
                                    start_at: '',
                                    duration: ''
                                  }));
                                  fetchAvailableTimeSlots(reservation.bike_id, originalDate, reservation.id, reservation);
                                }
                              }}
                            />
                            <span>Alle nog komende keren</span>
                          </label>
                          {editScope === 'series' && (
                            <small className="help-text">
                              Bij alle komende keren kun je alleen de tijd aanpassen, niet de datum.
                              Elke keer houdt zijn eigen dag en krijgt de nieuwe tijd. Keren die op de
                              nieuwe tijd al bezet zijn, blijven ongewijzigd. Wil je een andere dag van
                              de week? Annuleer dan de komende keren en plan een nieuwe herhaling.
                            </small>
                          )}
                        </fieldset>
                      )}

                      <div className="edit-inputs">
                        <div className="input-group">
                          <label>Datum *</label>
                          <input
                            type="date"
                            value={editForm.selectedDate}
                            disabled={editScope === 'series' && Boolean(reservation.series_id)}
                            onChange={(e) => {
                              const newDate = e.target.value;
                              setEditForm(prev => ({ 
                                ...prev, 
                                selectedDate: newDate,
                                start_at: '',
                                duration: ''
                              }));
                              // Pass the reservation so the current time is included if the date matches
                              fetchAvailableTimeSlots(reservation.bike_id, newDate, reservation.id, reservation);
                            }}
                            required
                          />
                        </div>
                        
                        {isLoadingTimeSlots ? (
                          <div className="loading-time-slots">
                            <p>⏳ Beschikbare tijden worden geladen...</p>
                          </div>
                        ) : availableTimeSlots && availableTimeSlots.start_time_options.length > 0 ? (
                          <>
                            <div className="input-group">
                              <label>Start Tijd *</label>
                              <select
                                value={editForm.start_at}
                                onChange={(e) => {
                                  setEditForm(prev => ({
                                    ...prev,
                                    start_at: e.target.value,
                                    duration: ''
                                  }));
                                }}
                                required
                              >
                                <option value="">Selecteer start tijd</option>
                                {availableTimeSlots.start_time_options
                                  .filter(option => option.available)
                                  .map((option, index) => (
                                    <option key={index} value={option.start}>
                                      {option.display} (Max {formatDuration(option.max_duration)})
                                    </option>
                                  ))}
                              </select>
                            </div>
                            
                            <div className="input-group">
                              <label>Duur (uren) *</label>
                              <select
                                value={editForm.duration}
                                onChange={(e) => setEditForm(prev => ({ ...prev, duration: e.target.value }))}
                                required
                                disabled={!editForm.start_at}
                              >
                                <option value="">Selecteer eerst een start tijd</option>
                                {(() => {
                                  if (!editForm.start_at || !availableTimeSlots) {
                                    return null;
                                  }
                                  
                                  const selectedStartTime = availableTimeSlots.start_time_options
                                    .filter(option => option.available)
                                    .find(option => option.start === editForm.start_at);
                                  
                                  if (!selectedStartTime) {
                                    return null;
                                  }
                                  
                                  const durations = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3, 3.25, 3.5, 3.75, 4, 4.25, 4.5, 4.75, 5, 5.25, 5.5, 5.75, 6, 6.25, 6.5, 6.75, 7];
                                  return durations
                                    .filter(duration => duration <= selectedStartTime.max_duration)
                                    .map(duration => (
                                      <option key={duration} value={duration.toString()}>
                                        {formatDuration(duration)}
                                      </option>
                                    ));
                                })()}
                              </select>
                            </div>
                          </>
                        ) : editForm.selectedDate ? (
                          <div className="no-time-slots">
                            <p>⚠️ Geen beschikbare tijden gevonden voor deze datum.</p>
                          </div>
                        ) : null}
                        
                        <div className="input-group">
                          <label>Opmerkingen</label>
                          <input
                            type="text"
                            value={editForm.notes}
                            onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
                          />
                        </div>
                      </div>
                      <div className="edit-actions">
                        <button 
                          className="button secondary"
                          onClick={cancelEditing}
                          disabled={loading}
                        >
                          Annuleren
                        </button>
                        <button 
                          className="button primary"
                          onClick={() => updateReservation(reservation.id)}
                          disabled={loading || !editForm.start_at || !editForm.duration}
                        >
                          {loading ? 'Bijwerken...' : 'Bijwerken'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="reservation-actions">
                      <div className="action-buttons">
                        <button
                          className="button secondary"
                          onClick={() => startEditing(reservation)}
                          disabled={loading}
                        >
                          Bewerken
                        </button>
                        {reservation.status !== 'cancelled' && reservation.status !== 'completed' && (
                          <button
                            className="button warning"
                            onClick={() => openCancelModal(reservation)}
                            disabled={loading}
                          >
                            Annuleren
                          </button>
                        )}
                        {user?.isAdmin && (
                          <button
                            className="button danger"
                            onClick={() => deleteReservation(reservation.id)}
                            disabled={loading}
                          >
                            Verwijderen
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {reservations.length === 0 && !loading && hasSearched && !error && (
          <div className="no-reservations">
            <p>Geen reserveringen gevonden voor dit email adres.</p>
            <p>Controleer of je het juiste email adres hebt ingevoerd.</p>
          </div>
        )}
      </div>

      {/* Cancel Reservation Modal */}
      {showCancelModal && cancellingReservation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Reservering Annuleren</h3>
              <button 
                className="modal-close"
                onClick={() => setShowCancelModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ textAlign: 'left' }}>
              <p style={{ textAlign: 'left' }}><strong>Status:</strong> {getStatusLabel(cancellingReservation.status)}</p>
              <p style={{ textAlign: 'left' }}><strong>Fiets:</strong> {getBikeInfo(cancellingReservation.bike_id)}</p>
              <p style={{ textAlign: 'left' }}><strong>Locatie:</strong> {getLocationInfo(cancellingReservation.bike_id)}</p>
              <p style={{ textAlign: 'left' }}><strong>Start:</strong> {formatAmsterdam(cancellingReservation.start_at, 'DD-MM-YYYY HH:mm')}</p>
              <p style={{ textAlign: 'left' }}><strong>Eind:</strong> {formatAmsterdam(cancellingReservation.end_at, 'DD-MM-YYYY HH:mm')}</p>

              {cancellingReservation.series_id && (
                <fieldset className="scope-choice">
                  <legend>Wat wil je annuleren?</legend>
                  <label>
                    <input
                      type="radio"
                      name="cancel-scope"
                      checked={cancelScope === 'single'}
                      onChange={() => setCancelScope('single')}
                    />
                    <span>Alleen deze keer</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="cancel-scope"
                      checked={cancelScope === 'series'}
                      onChange={() => setCancelScope('series')}
                    />
                    <span>Alle nog komende keren van deze herhaling</span>
                  </label>
                  {cancelScope === 'series' && (
                    <small className="help-text">
                      Keren die al geweest zijn, blijven staan. Je krijgt één e-mail met een
                      overzicht van alles wat is geannuleerd.
                    </small>
                  )}
                </fieldset>
              )}

              <div className="form-group">
                <label htmlFor="cancellation-reason">
                  Reden voor annulering <span className="required">*</span>
                </label>
                <textarea
                  id="cancellation-reason"
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Voer de reden voor annulering in..."
                  rows={4}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="button secondary"
                onClick={() => setShowCancelModal(false)}
              >
                Annuleren
              </button>
              <button 
                className="button danger"
                onClick={cancelReservation}
                disabled={!cancellationReason.trim() || loading}
              >
                {loading ? 'Annuleren...' : 'Reservering Annuleren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditReservation;
